import { analysisComparisonRange, type AnalysisCalendarPeriod } from "./analysis-date-range";
import { shiftProductionLineNames, type ShiftFormManagerDashboard, type ShiftFormManagerDashboardFilters } from "../../../shared/shift-production-form/contracts";
import { buildAnalysisDepartments } from "./analysis-departments";

export type FactoryMetricId = "OUTPUT" | "DOWNTIME" | "SCRAP";
export type FactoryMetricState = "RECORDED" | "MISSING" | "EMPTY" | "UNAVAILABLE";
export type FactoryMetricComparison =
  | { kind: "CHANGE"; percent: number; direction: "UP" | "DOWN" | "SAME" }
  | { kind: "NO_PREVIOUS" }
  | { kind: "PREVIOUS_EMPTY" }
  | { kind: "PREVIOUS_MISSING" };

export type FactoryMetric = {
  id: FactoryMetricId;
  label: string;
  unit: "koli" | "dk" | "kg";
  value: number | null;
  state: FactoryMetricState;
  comparison: FactoryMetricComparison | null;
};

export type FactorySummary = {
  filters: ShiftFormManagerDashboardFilters;
  previousFilters: ShiftFormManagerDashboardFilters | null;
  caption: string;
  periodLabel: string | null;
  previousPeriodLabel: string | null;
  metrics: FactoryMetric[];
};

const calendarDate = (value: string | undefined) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/u.test(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, (month ?? 1) - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === (month ?? 1) - 1 && date.getUTCDate() === day ? date : null;
};
const formatDate = (date: Date) => date.toISOString().slice(0, 10);
const round = (value: number) => Math.round(value * 100) / 100;
const displayDate = (value: string | undefined) => value ? value.split("-").reverse().join(".") : null;

/** Keeps the report-level scope while removing the current drilldown. */
export function analysisFactoryFilters(filters: ShiftFormManagerDashboardFilters): ShiftFormManagerDashboardFilters {
  return { ...filters, departmentName: undefined, lineName: undefined, offset: 0 };
}

/** Returns the same-length immediately preceding UTC calendar period. */
export function previousAnalysisPeriod(filters: ShiftFormManagerDashboardFilters, period?: AnalysisCalendarPeriod): ShiftFormManagerDashboardFilters | null {
  if (period) {
    const range = analysisComparisonRange({ dateFrom: filters.dateFrom ?? "", dateTo: filters.dateTo ?? "" }, period);
    return range ? { ...analysisFactoryFilters(filters), ...range } : null;
  }
  const from = calendarDate(filters.dateFrom);
  const to = calendarDate(filters.dateTo);
  if (!from || !to || from > to) return null;
  const duration = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
  const previousTo = new Date(from.getTime() - 86_400_000);
  const previousFrom = new Date(previousTo.getTime() - (duration - 1) * 86_400_000);
  return { ...analysisFactoryFilters(filters), dateFrom: formatDate(previousFrom), dateTo: formatDate(previousTo) };
}

function metricModel(dashboard: ShiftFormManagerDashboard | null, filters: ShiftFormManagerDashboardFilters, id: FactoryMetricId) {
  if (!dashboard) return { value: null, state: "UNAVAILABLE" as const };
  const measure = id === "OUTPUT" ? { kind: "OUTPUT" as const, unit: "koli" }
    : id === "DOWNTIME" ? { kind: "DOWNTIME" as const }
      : { kind: "WASTE" as const, key: "SCRAP" as const, label: "Iskarta", unit: "kg" };
  const model = buildAnalysisDepartments(dashboard, filters, measure);
  if (!model.available) return { value: null, state: "UNAVAILABLE" as const };
  if (model.unclassified || model.missing) return { value: null, state: "MISSING" as const };
  if (model.empty) return { value: null, state: "EMPTY" as const };
  if (model.total === null) return { value: null, state: "MISSING" as const };
  return { value: model.total, state: "RECORDED" as const };
}

function sameAccessibleLineScope(current: ShiftFormManagerDashboard | null, previous: ShiftFormManagerDashboard | null) {
  if (!current || !previous) return false;
  const currentLines = [...current.accessibleLineNames].sort();
  const previousLines = [...previous.accessibleLineNames].sort();
  return currentLines.length === previousLines.length && currentLines.every((line, index) => line === previousLines[index]);
}

function comparison(current: ReturnType<typeof metricModel>, previous: ReturnType<typeof metricModel> | null, comparableScope: boolean): FactoryMetricComparison | null {
  if (!previous) return { kind: "NO_PREVIOUS" };
  if (previous.state === "EMPTY") return { kind: "PREVIOUS_EMPTY" };
  if (!comparableScope || previous.state !== "RECORDED" || current.state !== "RECORDED" || current.value === null || previous.value === null || previous.value <= 0) return { kind: "PREVIOUS_MISSING" };
  const percent = round((current.value - previous.value) / previous.value * 100);
  return { kind: "CHANGE", percent, direction: percent === 0 ? "SAME" : percent > 0 ? "UP" : "DOWN" };
}

function isFactoryWideScope(dashboard: ShiftFormManagerDashboard | null) {
  return Boolean(dashboard && shiftProductionLineNames.every(line => dashboard.accessibleLineNames.includes(line)));
}

/**
 * Builds the fixed report header only from complete comparison analytics.
 * It deliberately never uses the bounded source-form rows.
 */
export function buildFactorySummary(
  current: ShiftFormManagerDashboard | null,
  previous: ShiftFormManagerDashboard | null,
  filters: ShiftFormManagerDashboardFilters,
  period?: AnalysisCalendarPeriod,
): FactorySummary {
  const factoryFilters = { ...analysisFactoryFilters(filters), departmentName: filters.departmentName };
  const prior = previousAnalysisPeriod(factoryFilters, period);
  const previousFilters = prior ? { ...prior, departmentName: factoryFilters.departmentName } : null;
  const previousPeriodLabel = previousFilters?.dateFrom && previousFilters.dateTo ? previousFilters.dateFrom === previousFilters.dateTo
    ? displayDate(previousFilters.dateFrom)
    : `${displayDate(previousFilters.dateFrom)}–${displayDate(previousFilters.dateTo)}` : null;
  const comparableScope = sameAccessibleLineScope(current, previous);
  const definitions: Array<Pick<FactoryMetric, "id" | "label" | "unit">> = [
    { id: "OUTPUT", label: "Üretim", unit: "koli" },
    { id: "DOWNTIME", label: "Bildirilen duruş", unit: "dk" },
    { id: "SCRAP", label: "Iskarta", unit: "kg" },
  ];
  return {
    filters: factoryFilters,
    previousFilters,
    caption: factoryFilters.departmentName ?? (isFactoryWideScope(current) ? "Fabrika geneli" : "Genel kapsam"),
    periodLabel: factoryFilters.dateFrom && factoryFilters.dateTo ? factoryFilters.dateFrom === factoryFilters.dateTo
      ? displayDate(factoryFilters.dateFrom)
      : `${displayDate(factoryFilters.dateFrom)}–${displayDate(factoryFilters.dateTo)}` : null,
    previousPeriodLabel,
    metrics: definitions.map(definition => {
      const currentMetric = metricModel(current, factoryFilters, definition.id);
      const previousMetric = previousFilters ? metricModel(previous, previousFilters, definition.id) : null;
      return { ...definition, ...currentMetric, comparison: comparison(currentMetric, previousMetric, comparableScope) };
    }),
  };
}
